import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import {
    ContactPageIcon,
    VisibilityIcon,
    LinkIcon,
    PersonAddIcon
} from 'icons/materialIcons';

const ActivityItem = ({ type, user, action, timestamp, metadata }) => {
    // Debug logging to understand metadata structure
    console.log('🎯 ActivityItem Debug:', {
        type,
        user,
        metadata,
        metadataKeys: Object.keys(metadata || {}),
        profilePictureUrl: metadata?.profilePictureUrl,
        targetPhoto: metadata?.targetPhoto,
        actorPhoto: metadata?.actorPhoto,
        leadPhoto: metadata?.leadPhoto,
        cardHolderPhoto: metadata?.cardHolderPhoto
    });

    // Map activity types to appropriate icons and colors
    const getActivityIcon = (activityType) => {
        switch (activityType) {
            case 'lead':
            case 'lead_capture':
            case 'lead_captured':
                return <PersonAddIcon sx={{ color: '#4CAF50' }} />;
            case 'card_view':
            case 'view':
                return <VisibilityIcon sx={{ color: '#2196F3' }} />;
            case 'link_tap':
            case 'tap':
                return <LinkIcon sx={{ color: '#9C27B0' }} />;
            case 'contact_download':
            case 'download':
                return <ContactPageIcon sx={{ color: '#FF9800' }} />;
            default:
                return <ContactPageIcon sx={{ color: '#9E9E9E' }} />;
        }
    };    // Get avatar content - show profile pictures based on activity type
    const getAvatarContent = () => {
        let profilePictureUrl = null;

        // Enhanced profile picture detection with better fallback logic
        if (type === 'card_view' || type === 'view') {
            // For card views, use the card holder's profile picture from multiple sources
            profilePictureUrl = metadata?.targetPhoto ||
                metadata?.cardHolderPhoto ||
                metadata?.profilePictureUrl ||
                metadata?.profilePicture ||
                metadata?.cardHolderInfo?.profilePicture;
        } else if (type === 'lead_capture' || type === 'lead') {
            // For lead activities, use the lead's profile picture
            profilePictureUrl = metadata?.leadPhoto ||
                metadata?.leadPicture ||
                metadata?.actorPhoto ||
                metadata?.profilePictureUrl ||
                metadata?.profilePicture;
        } else if (type === 'link_tap' || type === 'tap') {
            // For link taps, show the card holder's picture
            profilePictureUrl = metadata?.targetPhoto ||
                metadata?.cardHolderPhoto ||
                metadata?.profilePictureUrl ||
                metadata?.profilePicture ||
                metadata?.cardHolderInfo?.profilePicture;
        } else if (type === 'contact_download' || type === 'download') {
            // For contact downloads, show the card holder's picture
            profilePictureUrl = metadata?.targetPhoto ||
                metadata?.cardHolderPhoto ||
                metadata?.profilePictureUrl ||
                metadata?.profilePicture ||
                metadata?.cardHolderInfo?.profilePicture;
        } else {
            // For other activities, try various metadata sources
            profilePictureUrl = metadata?.profilePictureUrl ||
                metadata?.profilePicture ||
                metadata?.photo ||
                metadata?.avatar ||
                metadata?.image ||
                metadata?.targetPhoto ||
                metadata?.actorPhoto;
        }

        // Enhanced URL validation
        const isValidImageUrl = (url) => {
            if (!url || typeof url !== 'string') return false;
            if (url.includes('placeholder') || url.includes('default')) return false;
            // Allow more flexible image URL patterns
            return url.match(/\.(jpeg|jpg|gif|png|webp|svg|bmp|tiff)$/i) ||
                url.includes('blob:') ||
                url.includes('data:image/') ||
                url.includes('googleapis.com') ||
                url.includes('cloudinary.com') ||
                url.includes('amazonaws.com') ||
                url.startsWith('http') && !url.includes('undefined');
        };

        console.log('🖼️ Avatar Debug:', {
            type,
            profilePictureUrl,
            isValid: isValidImageUrl(profilePictureUrl),
            metadata
        });

        if (profilePictureUrl && isValidImageUrl(profilePictureUrl)) {
            return (
                <Avatar
                    src={profilePictureUrl}
                    sx={{
                        width: 35,
                        height: 35,
                        marginRight: '10px'
                    }}
                    onError={(e) => {
                        console.warn('🚨 Image failed to load:', profilePictureUrl);
                        // If image fails to load, hide the broken image and show fallback
                        const avatarElement = e.target.closest('.MuiAvatar-root');
                        if (avatarElement) {
                            avatarElement.style.backgroundImage = 'none';
                            avatarElement.innerHTML = getInitialsAvatar();
                        }
                    }}
                />
            );
        }

        // Fallback to initials avatar for all activity types when no profile picture is available
        return (
            <Avatar
                sx={{
                    width: 35,
                    height: 35,
                    marginRight: '10px',
                    backgroundColor: '#e0e0e0',
                    color: '#666'
                }}
            >
                {getInitialsAvatar()}
            </Avatar>
        );
    };

    // Helper function to generate initials
    const getInitialsAvatar = () => {
        if (user && user !== 'Someone') {
            return user.split(' ').map(name => name.charAt(0)).join('').slice(0, 2).toUpperCase();
        }
        return '?';
    };

    // Format timestamp to a human-readable format
    const formatTimestamp = (ts) => {
        if (!ts) return '';

        try {
            const date = new Date(ts);
            if (isNaN(date.getTime())) return '';

            // If today, show time
            const today = new Date();
            if (date.toDateString() === today.toDateString()) {
                return date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true
                });
            }

            // Otherwise show date
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting timestamp:', error);
            return '';
        }
    };

    return (<Box
        sx={{
            display: 'flex',
            padding: '0 20px 18px 36px',
            alignItems: 'center'
        }}
    >
        {getAvatarContent()}
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1
            }}
        >            <Typography
            sx={{
                fontSize: '13px',
                fontWeight: 500
            }}
        >                {/* Enhanced text formatting with bold names */}                {(() => {
            const fullText = user + (action ? ` ${action}` : '');
            console.group('🔍 ACTIVITY ITEM RENDER');
            console.log('1. Full text:', fullText);
            console.log('2. User:', user);
            console.log('3. Action:', action);
            console.log('4. Type:', type);
            console.log('5. Metadata:', metadata);

            // Handle patterns like "Jane's card was viewed" - make "Jane's" bold
            const nameCardPattern = /^([^']+)'s\s(card|contact|Email|Phone|Website|LinkedIn|Instagram|Twitter|Facebook|YouTube|TikTok|WhatsApp|Connect)\s(.+)$/;
            const cardMatch = fullText.match(nameCardPattern);

            if (cardMatch) {
                const [, name, item, rest] = cardMatch;
                console.log('6a. Name card pattern match:', { name, item, rest });
                return (
                    <>
                        <span style={{ fontWeight: 700 }}>{name}'s</span>
                        {` ${item} ${rest}`}
                    </>
                );
            }

            // Handle patterns like "Your card was viewed" or "Your contact was downloaded"
            const yourPattern = /^Your\s(.+)$/;
            const yourMatch = fullText.match(yourPattern);

            if (yourMatch) {
                const [, rest] = yourMatch;
                console.log('6b. Your pattern match:', { rest });
                return (
                    <>
                        <span style={{ fontWeight: 700 }}>Your</span>
                        {` ${rest}`}
                    </>
                );
            }

            // Handle patterns like "Mary connected with John" - make both names bold
            const connectedPattern = /^(.+?)\s+connected\s+with\s+(.+)$/;
            const connectedMatch = fullText.match(connectedPattern);

            if (connectedMatch) {
                const [, actor, target] = connectedMatch;
                console.log('6c. Connected pattern match:', { actor, target });

                // Get the card holder name from metadata if available
                const cardHolderName = metadata?.cardHolderName || metadata?.cardOwnerName || metadata?.cardName;
                console.log('7. Card holder name from metadata:', cardHolderName);

                // If we have a card holder name in metadata, use it instead of the target
                if (cardHolderName) {
                    console.log('8a. Using card holder name from metadata');
                    return (
                        <>
                            <span style={{ fontWeight: 700 }}>{actor}</span>
                            {' connected with '}
                            <span style={{ fontWeight: 700 }}>{cardHolderName}</span>
                        </>
                    );
                }

                // Otherwise, use the target from the action string
                console.log('8b. Using target from action string');
                return (
                    <>
                        <span style={{ fontWeight: 700 }}>{actor}</span>
                        {' connected with '}
                        <span style={{ fontWeight: 700 }}>{target}</span>
                    </>
                );
            }

            // Default case - just make the user name bold
            console.log('9. Using default formatting');
            return (
                <>
                    <span style={{ fontWeight: 700 }}>{user}</span>
                    {action ? ` ${action}` : ''}
                </>
            );
        })()}
            </Typography>
        </Box>
        <Typography
            sx={{
                fontSize: '12px',
                color: '#828282'
            }}
        >
            {formatTimestamp(timestamp)}
        </Typography>
    </Box>
    );
};

export default ActivityItem;